import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PatientPicker } from "@/components/opd/PatientPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Stethoscope, Receipt, Loader2, Ticket, Clock, CheckCircle2, IndianRupee, Users } from "lucide-react";
import { toast } from "sonner";

const Q_STATUS = { waiting: "waiting", in_consultation: "provisioning", completed: "active", cancelled: "inactive" };

export default function OpdQueue() {
  const { activeId, has } = useFacility();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ doctors: [], departments: [] });
  const [doctorFilter, setDoctorFilter] = useState("");
  const [defaulted, setDefaulted] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ patient: null, doctor_id: "", department: "", visit_type: "new", chief_complaint: "", fee: 500 });

  const myId = auth?.user?.id;
  const iAmDoctor = myId && meta.doctors.some((d) => d.id === myId);

  const load = useCallback(() => {
    if (!activeId) return;
    setRows(null);
    api.get(`/opd/visits?facility_id=${activeId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get(`/opd/stats?facility_id=${activeId}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/opd/meta?facility_id=${activeId}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [activeId]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!defaulted && meta.doctors.length && iAmDoctor) { setDoctorFilter(myId); }
    if (meta.doctors.length) setDefaulted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.doctors, iAmDoctor]);

  const filteredRows = rows === null ? null : rows.filter((v) => {
    if (!doctorFilter) return true;
    if (doctorFilter === "unassigned") return !v.doctor_id;
    return v.doctor_id === doctorFilter;
  });

  useEffect(() => {
    const pid = searchParams.get("patient");
    if (!pid) return;
    api.get(`/opd/patients/${pid}`).then((r) => {
      setForm((f) => ({ ...f, patient: r.data }));
      setOpen(true);
    }).catch(() => {});
    searchParams.delete("patient");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const createVisit = async () => {
    if (!form.patient) { toast.error("Select a patient"); return; }
    setSaving(true);
    try {
      const doc = meta.doctors.find((d) => d.id === form.doctor_id);
      await api.post("/opd/visits", { patient_id: form.patient.id, doctor_id: form.doctor_id || null,
        doctor_name: doc?.name || null, department: form.department, visit_type: form.visit_type,
        chief_complaint: form.chief_complaint, fee: parseFloat(form.fee || 0) });
      toast.success("Token generated");
      setOpen(false);
      setForm({ patient: null, doctor_id: "", department: "", visit_type: "new", chief_complaint: "", fee: 500 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const setStatus = async (v, status) => {
    try { await api.patch(`/opd/visits/${v.id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startConsult = async (v) => { if (v.status === "waiting") await setStatus(v, "in_consultation"); navigate(`/app/opd/consultation/${v.id}`); };

  return (
    <div data-testid="opd-queue-page">
      <PageHeader title="OPD Queue" subtitle="Today's outpatient tokens and live consultation status."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 h-9">
              <span className="text-xs text-slate-500 font-medium">Doctor</span>
              <select
                className="h-8 text-sm bg-transparent focus:outline-none text-slate-700"
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
                data-testid="queue-doctor-filter"
              >
                <option value="">All doctors</option>
                <option value="unassigned">Unassigned</option>
                {meta.doctors.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.id === myId ? " (me)" : ""}</option>
                ))}
              </select>
            </div>
            {has("opd:create") && <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="new-visit-button"><Plus className="h-4 w-4 mr-1.5" /> Generate Token</Button>}
          </div>
        } />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label="Today's Tokens" value={stats.today_tokens} icon={Ticket} testId="opd-stat-tokens" />
          <StatCard label="Waiting" value={stats.waiting} icon={Clock} tone="amber" testId="opd-stat-waiting" />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} tone="emerald" testId="opd-stat-completed" />
          <StatCard label="Revenue Today" value={`₹${stats.revenue_today.toLocaleString()}`} icon={IndianRupee} tone="slate" testId="opd-stat-revenue" />
          <StatCard label="Total Patients" value={stats.total_patients} icon={Users} tone="slate" testId="opd-stat-patients" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Token</TableHead><TableHead>Patient</TableHead><TableHead>Doctor</TableHead><TableHead>Department</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {filteredRows.map((v) => (
                <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`visit-row-${v.id}`}>
                  <TableCell><span className="font-mono font-bold text-blue-700 text-base">#{v.token_number}</span></TableCell>
                  <TableCell><div className="font-medium text-slate-800">{v.patient_name}</div><div className="text-xs text-slate-400 font-mono">{v.uhid}</div></TableCell>
                  <TableCell className="text-sm text-slate-600">{v.doctor_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{v.department || "—"}</TableCell>
                  <TableCell><span className="text-xs capitalize px-2 py-0.5 rounded bg-slate-100 text-slate-600">{v.visit_type?.replace("_", " ")}</span></TableCell>
                  <TableCell><StatusBadge status={Q_STATUS[v.status] || "inactive"} label={v.status?.replace("_", " ")} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {has("consultation:view") && <Button variant="outline" size="sm" onClick={() => startConsult(v)} data-testid={`consult-${v.id}`}><Stethoscope className="h-3.5 w-3.5 mr-1.5" /> Consult</Button>}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`visit-actions-${v.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setStatus(v, "completed")}><CheckCircle2 className="h-4 w-4 mr-2" /> Mark completed</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatus(v, "waiting")}><Clock className="h-4 w-4 mr-2" /> Back to waiting</DropdownMenuItem>
                          {has("opd_billing:create") && <DropdownMenuItem onClick={() => navigate(`/app/opd/billing?patient=${v.patient_id}&visit=${v.id}&fee=${v.fee || 0}`)} data-testid={`bill-${v.id}`}><Receipt className="h-4 w-4 mr-2" /> Create invoice</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => setStatus(v, "cancelled")} className="text-red-600">Cancel visit</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-12">{rows.length === 0 ? "No tokens yet today. Create a new visit to start the queue." : "No patients match this doctor filter."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-visit-dialog">
          <DialogHeader><DialogTitle>New OPD Visit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Patient</Label><PatientPicker value={form.patient} onChange={(p) => setForm({ ...form, patient: p })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Doctor</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })} data-testid="visit-doctor-select">
                  <option value="">Unassigned</option>
                  {meta.doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.designation ? ` — ${d.designation}` : ""}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Department</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="visit-dept-select">
                  <option value="">Select…</option>
                  {meta.departments.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Visit type</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} data-testid="visit-type-select">
                  <option value="new">New</option><option value="follow_up">Follow-up</option>
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Consultation fee (₹)</Label><Input type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="visit-fee-input" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Chief complaint</Label><Input value={form.chief_complaint} onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} data-testid="visit-complaint-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={createVisit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="visit-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate token"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
